import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SectionHelp from '@/components/help/SectionHelp';
import EmptyStateGuide from '@/components/help/EmptyStateGuide';
import SensitiveActionHelp from '@/components/help/SensitiveActionHelp';
import sensitiveActions from '@/help/sensitiveActions';
import { Plus, Check, Trash2, UserCheck, Send, ClipboardList, Zap, X, Pencil } from 'lucide-react';
import TaskFunctionLogic, { summarizeFunctionConfig } from '@/components/crm/TaskFunctionLogic';
import { emptyTaskForm, taskToForm, formToPayload, validateTaskForm } from '@/components/crm/crmTaskFormUtils';

export default function CrmTasks() {
  const [searchParams] = useSearchParams();
  const { filterQuery } = useOutletContext() || {};
  const prefillLeadId = searchParams.get('lead_id') || '';
  const [tasks, setTasks] = useState([]);
  const [leads, setLeads] = useState([]);
  const [deals, setDeals] = useState([]);
  const [agents, setAgents] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [taskFunctions, setTaskFunctions] = useState([]);
  const [functionsModuleOn, setFunctionsModuleOn] = useState(true);
  const [waTemplates, setWaTemplates] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(!!prefillLeadId);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyTaskForm({ crm_lead_id: prefillLeadId }));
  const [notifyOnTask, setNotifyOnTask] = useState(false);
  const [notifyChannel, setNotifyChannel] = useState('whatsapp');
  const [notifyTemplateId, setNotifyTemplateId] = useState('');
  const [saving, setSaving] = useState(false);

  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', steps: [{ title: '', description: '', due_offset_days: 0 }] });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showApplyFor, setShowApplyFor] = useState(null);
  const [applyForm, setApplyForm] = useState({ crm_lead_id: '', crm_deal_id: '', assigned_to_user_id: '' });
  const [applying, setApplying] = useState(false);

  const load = async () => {
    try {
      // El selector de lead de esta pantalla (para crear una tarea nueva) se
      // acota al mismo rango de "mes actual" que Leads/Pipeline — igual que
      // ahí, evita traer toda la tabla de leads de un cliente con años de
      // historial acumulado. Las tareas YA creadas (tasks?status=all) no se
      // acotan: una tarea vieja pendiente debe seguir viéndose aunque su
      // lead sea de hace tiempo, solo pierde el nombre de lead junto al
      // título (leadMap más abajo) si ese lead cae fuera del rango.
      const leadsUrl = `/tenant/crm/leads${filterQuery ? `?${filterQuery}` : ''}`;
      const [tRes, lRes, dRes, aRes, wRes, eRes, tmplRes, cRes, fRes] = await Promise.all([
        api.get('/tenant/crm/tasks?status=all'),
        api.get(leadsUrl).catch(() => ({ data: { items: [] } })),
        api.get('/tenant/crm/deals?status=open').catch(() => ({ data: { items: [] } })),
        api.get('/tenant/assignment/status').catch(() => ({ data: { agents_load: [] } })),
        api.get('/tenant/marketing/templates').catch(() => ({ data: [] })),
        api.get('/tenant/email/templates?status=active').catch(() => ({ data: [] })),
        api.get('/tenant/crm/task-templates').catch(() => ({ data: { items: [] } })),
        api.get('/tenant/catalog/items?page_size=100').catch(() => ({ data: [] })),
        api.get('/tenant/crm/task-functions').catch(() => ({ data: { items: [], module_enabled: false } })),
      ]);
      setTasks(tRes.data?.items || []);
      setLeads(lRes.data?.items || []);
      setDeals(dRes.data?.items || []);
      setAgents(aRes.data?.agents_load || []);
      setWaTemplates((Array.isArray(wRes.data) ? wRes.data : []).filter(t => t.external_status === 'APPROVED'));
      setEmailTemplates(Array.isArray(eRes.data) ? eRes.data : []);
      setTemplates(tmplRes.data?.items || []);
      const cat = cRes.data?.items || cRes.data || [];
      setCatalog(Array.isArray(cat) ? cat : []);
      setTaskFunctions(fRes.data?.items || []);
      setFunctionsModuleOn(fRes.data?.module_enabled !== false);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const addTemplateStep = () => setTemplateForm(f => ({ ...f, steps: [...f.steps, { title: '', description: '', due_offset_days: 0 }] }));
  const removeTemplateStep = (i) => setTemplateForm(f => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));
  const updateTemplateStep = (i, key, val) => setTemplateForm(f => ({ ...f, steps: f.steps.map((s, idx) => idx === i ? { ...s, [key]: val } : s) }));

  const saveTemplate = async () => {
    if (!templateForm.name.trim()) { toast.error('Nombre de la plantilla requerido'); return; }
    if (templateForm.steps.some(s => !s.title.trim())) { toast.error('Cada tarea de la plantilla necesita un título'); return; }
    setSavingTemplate(true);
    try {
      await api.post('/tenant/crm/task-templates', {
        name: templateForm.name, description: templateForm.description,
        steps: templateForm.steps.map(s => ({ ...s, due_offset_days: Number(s.due_offset_days) || 0 })),
      });
      toast.success('Plantilla creada');
      setTemplateForm({ name: '', description: '', steps: [{ title: '', description: '', due_offset_days: 0 }] });
      setShowTemplateForm(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
    setSavingTemplate(false);
  };

  const deleteTemplate = async (id) => {
    try { await api.delete(`/tenant/crm/task-templates/${id}`); toast.success('Plantilla eliminada'); load(); } catch { toast.error('Error'); }
  };

  const applyTemplate = async (id) => {
    if (!applyForm.crm_lead_id && !applyForm.crm_deal_id) { toast.error('Elige un lead o una oportunidad'); return; }
    setApplying(true);
    try {
      const res = await api.post(`/tenant/crm/task-templates/${id}/apply`, {
        crm_lead_id: applyForm.crm_lead_id || null,
        crm_deal_id: applyForm.crm_deal_id || null,
        assigned_to_user_id: applyForm.assigned_to_user_id || null,
      });
      toast.success(`${res.data.created_count} tareas creadas`);
      setShowApplyFor(null); setApplyForm({ crm_lead_id: '', crm_deal_id: '', assigned_to_user_id: '' });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
    setApplying(false);
  };

  useEffect(() => { load(); }, [filterQuery]);

  const selectedLead = leads.find(l => l.id === form.crm_lead_id);
  const selectedFn = taskFunctions.find(f => f.key === form.function_key);
  const catalogMap = Object.fromEntries(catalog.map(c => [c.id, c]));
  const fnMap = Object.fromEntries(taskFunctions.map(f => [f.key, f]));

  const openNewForm = () => {
    setEditingId(null);
    setForm(emptyTaskForm({ crm_lead_id: prefillLeadId || '' }));
    setShowForm(true);
  };

  const openEdit = (task) => {
    setEditingId(task.id);
    setForm(taskToForm(task));
    setShowForm(true);
    setNotifyOnTask(false);
  };

  const handleSave = async () => {
    const err = validateTaskForm(form);
    if (err) { toast.error(err); return; }
    if (form.function_key && selectedFn?.locked) {
      toast.error(selectedFn.lock_reason || 'Función no disponible en tu plan');
      return;
    }
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (editingId) {
        await api.put(`/tenant/crm/tasks/${editingId}`, payload);
        toast.success('Tarea actualizada');
      } else {
        await api.post('/tenant/crm/tasks', payload);
        toast.success('Tarea creada' + (form.crm_lead_id && !form.assigned_to_user_id ? ' (responsable = del lead si tiene)' : ''));
        if (notifyOnTask && notifyTemplateId && form.crm_lead_id) {
          try {
            await api.post(`/tenant/crm/leads/${form.crm_lead_id}/notify`, { channel: notifyChannel, template_id: notifyTemplateId });
            toast.success('Notificación enviada al lead');
          } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || 'No se pudo enviar la notificación'); }
        }
      }
      setForm(emptyTaskForm());
      setEditingId(null);
      setNotifyOnTask(false); setNotifyTemplateId('');
      setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
    setSaving(false);
  };

  const complete = async (id) => {
    try { await api.patch(`/tenant/crm/tasks/${id}/complete`); toast.success('Tarea completada'); if (editingId === id) { setShowForm(false); setEditingId(null); } load(); } catch { toast.error('Error'); }
  };

  const remove = async (id) => {
    try { await api.delete(`/tenant/crm/tasks/${id}`); toast.success('Tarea eliminada'); if (editingId === id) { setShowForm(false); setEditingId(null); } load(); } catch { toast.error('Error'); }
  };

  if (loading) return <PantallaCargando />;

  const pending = tasks.filter(t => t.status === 'pending');
  const done = tasks.filter(t => t.status === 'done');
  const leadMap = Object.fromEntries(leads.map(l => [l.id, l.display_name]));
  const dealMap = Object.fromEntries(deals.map(d => [d.id, d.title]));
  const agentMap = Object.fromEntries(agents.map(a => [a.user_id, a.name]));

  return (
    <div className="space-y-4" data-testid="crm-tasks">
      <SectionHelp module="crm_tasks" />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => { if (showForm && !editingId) setShowForm(false); else openNewForm(); }} data-testid="crm-tasks-new">
          <Plus className="w-3.5 h-3.5 mr-1" /> Nueva tarea
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowTemplateForm(!showTemplateForm)} data-testid="crm-task-templates-new"><ClipboardList className="w-3.5 h-3.5 mr-1" /> Nueva plantilla</Button>
      </div>

      <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="crm-task-templates">
        <SectionHelp module="crm_task_templates" />
        {showTemplateForm && (
          <div className="space-y-3 border-b pb-4">
            <h3 className="text-sm font-bold">Nueva plantilla de tareas</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-xs text-[#64748B]">Nombre</label><Input value={templateForm.name} onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })} placeholder="Ej. Cliente nuevo" data-testid="template-name" /></div>
              <div><label className="text-xs text-[#64748B]">Descripción (opcional)</label><Input value={templateForm.description} onChange={e => setTemplateForm({ ...templateForm, description: e.target.value })} /></div>
            </div>
            <div className="space-y-2">
              <label className="text-xs text-[#64748B]">Tareas de la plantilla</label>
              {templateForm.steps.map((s, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input className="flex-1" placeholder="Título de la tarea" value={s.title} onChange={e => updateTemplateStep(i, 'title', e.target.value)} data-testid={`template-step-title-${i}`} />
                  <div className="flex items-center gap-1 shrink-0">
                    <Input type="number" className="w-16 text-xs" value={s.due_offset_days} onChange={e => updateTemplateStep(i, 'due_offset_days', e.target.value)} />
                    <span className="text-[10px] text-[#94A3B8] whitespace-nowrap">días después</span>
                  </div>
                  {templateForm.steps.length > 1 && <button onClick={() => removeTemplateStep(i)} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>}
                </div>
              ))}
              <Button size="sm" variant="ghost" onClick={addTemplateStep}><Plus className="w-3 h-3 mr-1" /> Agregar tarea a la plantilla</Button>
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setShowTemplateForm(false)}>Cancelar</Button>
              <Button size="sm" onClick={saveTemplate} disabled={savingTemplate} data-testid="template-save">{savingTemplate ? 'Guardando...' : 'Guardar plantilla'}</Button>
            </div>
          </div>
        )}

        {templates.length === 0 ? (
          <p className="text-xs text-[#94A3B8]">Aún no tienes plantillas. Crea una para aplicar varias tareas de un solo clic.</p>
        ) : (
          <div className="space-y-2">
            {templates.map(t => (
              <div key={t.id} className="border rounded-lg p-3" data-testid={`template-${t.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#0F172A]">{t.name}</p>
                    <p className="text-[10px] text-[#94A3B8]">{t.steps.length} tarea(s){t.description ? ` — ${t.description}` : ''}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setShowApplyFor(showApplyFor === t.id ? null : t.id)} data-testid={`template-apply-${t.id}`}><Zap className="w-3.5 h-3.5 mr-1" /> Aplicar</Button>
                    <SensitiveActionHelp action={sensitiveActions.crm_delete_task} onConfirm={() => deleteTemplate(t.id)}>
                      <button className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </SensitiveActionHelp>
                  </div>
                </div>
                {showApplyFor === t.id && (
                  <div className="mt-3 pt-3 border-t grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                    <div>
                      <label className="text-xs text-[#64748B]">Lead</label>
                      <Select value={applyForm.crm_lead_id || 'none'} onValueChange={v => setApplyForm({ ...applyForm, crm_lead_id: v === 'none' ? '' : v, crm_deal_id: '' })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Elegir lead..." /></SelectTrigger>
                        <SelectContent><SelectItem value="none">Ninguno</SelectItem>{leads.map(l => <SelectItem key={l.id} value={l.id}>{l.display_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-[#64748B]">Oportunidad</label>
                      <Select value={applyForm.crm_deal_id || 'none'} onValueChange={v => setApplyForm({ ...applyForm, crm_deal_id: v === 'none' ? '' : v, crm_lead_id: '' })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Elegir oportunidad..." /></SelectTrigger>
                        <SelectContent><SelectItem value="none">Ninguna</SelectItem>{deals.map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Select value={applyForm.assigned_to_user_id || 'none'} onValueChange={v => setApplyForm({ ...applyForm, assigned_to_user_id: v === 'none' ? '' : v })}>
                        <SelectTrigger className="h-8 text-xs flex-1"><SelectValue placeholder="Responsable..." /></SelectTrigger>
                        <SelectContent><SelectItem value="none">Sin asignar</SelectItem>{agents.map(a => <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="sm" onClick={() => applyTemplate(t.id)} disabled={applying} data-testid={`template-apply-confirm-${t.id}`}>{applying ? '...' : 'Crear tareas'}</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="crm-task-form">
          <h3 className="text-sm font-bold">{editingId ? 'Editar tarea' : 'Nueva tarea'}</h3>
          <p className="text-[11px] text-[#64748B]">Mismo módulo que el popup de la ficha del lead: crear, editar y reglas Si/No/Entonces.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className="text-xs text-[#64748B]">Título</label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="crm-task-title" /></div>
            <div>
              <label className="text-xs text-[#64748B]">Fecha y hora</label>
              <Input type="datetime-local" value={form.due_at} onChange={e => setForm({...form, due_at: e.target.value})} data-testid="crm-task-due-at" />
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Lead vinculado</label>
              <Select value={form.crm_lead_id || 'none'} onValueChange={v => setForm({...form, crm_lead_id: v === 'none' ? '' : v, assigned_to_user_id: ''})}>
                <SelectTrigger className="h-8 text-xs" data-testid="crm-task-lead-select"><SelectValue placeholder="Seleccionar lead..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ninguno</SelectItem>
                  {leads.map(l => <SelectItem key={l.id} value={l.id}>{l.display_name}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.crm_lead_id && (
                <p className="text-[10px] text-[#94A3B8] mt-0.5">Si el lead tiene vendedor y no eliges responsable, se le asigna a él (y a su Google Calendar si está conectado).</p>
              )}
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Oportunidad (catálogo: lo que le quieres vender)</label>
              <Select value={form.catalog_item_id || 'none'} onValueChange={v => setForm({...form, catalog_item_id: v === 'none' ? '' : v})} data-testid="crm-task-catalog-select">
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Producto o servicio…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin ítem de catálogo</SelectItem>
                  {catalog.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}{c.price_min != null ? ` · desde $${c.price_min}` : c.price != null ? ` · $${c.price}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#94A3B8] mt-0.5">No es otro lead: es el valor del producto/servicio de tu catálogo.</p>
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Deal en pipeline (opcional)</label>
              <Select value={form.crm_deal_id || 'none'} onValueChange={v => setForm({...form, crm_deal_id: v === 'none' ? '' : v})}>
                <SelectTrigger className="h-8 text-xs" data-testid="crm-task-deal-select"><SelectValue placeholder="Etapa de pipeline…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ninguno</SelectItem>
                  {deals.map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Responsable</label>
              <Select value={form.assigned_to_user_id || 'none'} onValueChange={v => setForm({...form, assigned_to_user_id: v === 'none' ? '' : v})}>
                <SelectTrigger className="h-8 text-xs" data-testid="crm-task-assignee-select"><SelectValue placeholder="Asignar a..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{form.crm_lead_id ? 'Automático (del lead)' : 'Sin asignar'}</SelectItem>
                  {agents.map(a => <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Actividad</label>
              <Input value={form.activity_label} onChange={e => setForm({...form, activity_label: e.target.value})} placeholder="Ej. Seguimiento comercial" data-testid="crm-task-activity" />
            </div>
            <div className="col-span-full"><label className="text-xs text-[#64748B]">Descripción / notas</label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
          </div>

          {functionsModuleOn && taskFunctions.length > 0 && (
            <div className="border rounded-lg p-3 bg-[#F8FAFC] space-y-1">
              <label className="text-xs font-medium text-[#334155]">Función (IA / automatización)</label>
              <Select
                value={form.function_key || 'none'}
                onValueChange={v => setForm({
                  ...form,
                  function_key: v === 'none' ? '' : v,
                  function_config: v === 'none' || v !== form.function_key ? {} : form.function_config,
                })}
              >
                <SelectTrigger className="h-8 text-xs" data-testid="crm-task-function-select"><SelectValue placeholder="Opcional…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin función</SelectItem>
                  {taskFunctions.map(f => (
                    <SelectItem key={f.key} value={f.key} disabled={f.locked}>
                      {f.locked ? '🔒 ' : ''}{f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedFn && (
                <TaskFunctionLogic
                  selectedFn={selectedFn}
                  functionConfig={form.function_config}
                  onConfigChange={cfg => setForm({ ...form, function_config: cfg })}
                  waTemplates={waTemplates}
                />
              )}
            </div>
          )}

          {!editingId && form.crm_lead_id && (selectedLead?.phone || selectedLead?.email) && (
            <div className="p-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg space-y-1.5">
              <label className="flex items-center gap-1.5 text-[11px] text-[#334155]">
                <input type="checkbox" checked={notifyOnTask} onChange={e => setNotifyOnTask(e.target.checked)} data-testid="crm-task-notify-toggle" />
                <Send className="w-3 h-3" /> Notificar al lead con una plantilla aprobada
              </label>
              {notifyOnTask && (
                <div className="flex gap-2 pl-5">
                  <Select value={notifyChannel} onValueChange={v => { setNotifyChannel(v); setNotifyTemplateId(''); }}>
                    <SelectTrigger className="h-7 text-xs w-28" data-testid="crm-task-notify-channel"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {selectedLead?.phone && <SelectItem value="whatsapp">WhatsApp</SelectItem>}
                      {selectedLead?.email && <SelectItem value="email">Correo</SelectItem>}
                    </SelectContent>
                  </Select>
                  <Select value={notifyTemplateId} onValueChange={setNotifyTemplateId}>
                    <SelectTrigger className="h-7 text-xs flex-1" data-testid="crm-task-notify-template"><SelectValue placeholder="Elige una plantilla..." /></SelectTrigger>
                    <SelectContent>
                      {(notifyChannel === 'whatsapp' ? waTemplates : emailTemplates).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); setForm(emptyTaskForm()); }}>Cancelar</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="crm-task-save">
              {saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Crear tarea'}
            </Button>
          </div>
        </div>
      )}

      {pending.length === 0 && done.length === 0 && (
        <EmptyStateGuide module="crm_tasks" message="No hay tareas. Crea una para dar seguimiento a tus leads." ctaLabel="Nueva tarea" onCta={openNewForm} />
      )}

      {pending.length > 0 && (
        <div className="space-y-2" data-testid="crm-tasks-pending">
          <h4 className="text-xs font-bold text-[#64748B]">Pendientes ({pending.length})</h4>
          {pending.map(t => {
            const cat = t.catalog_item_id ? catalogMap[t.catalog_item_id] : null;
            const fn = t.function_key ? fnMap[t.function_key] : null;
            const ruleSum = summarizeFunctionConfig(fn, t.function_config);
            return (
              <div key={t.id} className={`bg-white border rounded-lg px-4 py-2 flex items-start gap-3 ${editingId === t.id ? 'ring-2 ring-[#F72585]/30' : ''}`} data-testid={`task-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[#0F172A]">{t.title}</p>
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-[#64748B] mt-0.5">
                    {t.crm_lead_id && leadMap[t.crm_lead_id] && <span>Lead: {leadMap[t.crm_lead_id]}</span>}
                    {t.crm_deal_id && dealMap[t.crm_deal_id] && <span>Pipeline: {dealMap[t.crm_deal_id]}</span>}
                    {cat && <span>Oportunidad: {cat.name}</span>}
                    {fn && <span>Función: {fn.label}</span>}
                    {t.activity_label && <span>Actividad: {t.activity_label}</span>}
                    {t.assigned_to_user_id && agentMap[t.assigned_to_user_id] && <span className="flex items-center gap-0.5"><UserCheck className="w-2.5 h-2.5" /> {agentMap[t.assigned_to_user_id]}</span>}
                    {(t.due_at || t.due_date) && (
                      <span>Vence: {t.due_at ? new Date(t.due_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : t.due_date}</span>
                    )}
                  </div>
                  {ruleSum && <p className="text-[10px] text-[#94A3B8] mt-0.5">{ruleSum}</p>}
                  {t.description && <p className="text-[10px] text-[#94A3B8] mt-0.5">{t.description}</p>}
                </div>
                <button type="button" onClick={() => openEdit(t)} className="text-[#64748B] hover:text-[#0F172A]" title="Editar" data-testid={`task-edit-${t.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => complete(t.id)} className="text-emerald-500 hover:text-emerald-700" title="Completar"><Check className="w-4 h-4" /></button>
                <SensitiveActionHelp action={sensitiveActions.crm_delete_task} onConfirm={() => remove(t.id)}>
                  <button type="button" className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </SensitiveActionHelp>
              </div>
            );
          })}
        </div>
      )}

      {done.length > 0 && (
        <div className="space-y-1 mt-4">
          <h4 className="text-xs font-bold text-[#64748B]">Completadas ({done.length})</h4>
          {done.map(t => (
            <div key={t.id} className="px-4 py-1.5 text-xs text-[#94A3B8]">
              <span className="line-through">{t.title}</span>
              {t.function_key && fnMap[t.function_key] && <span className="ml-2 no-underline">· {fnMap[t.function_key].label}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
